/**
 * Role Configuration
 * Defines user roles, permissions, and access control for the SLAM Sports platform
 *
 * USAGE: Import where needed for role-based logic
 * import { ROLE_DEFINITIONS, ROLE_PERMISSIONS, hasRoleAccess } from '../config/roleConfig';
 */

// =============================================================================
// ROLE TYPE CONSTANTS
// =============================================================================
export const ROLE_TYPES = {
  ATHLETIC_DIRECTOR: 'ATHLETIC_DIRECTOR',
  HEAD_COACH: 'HEAD_COACH',
  ASSISTANT_COACH: 'ASSISTANT_COACH',
  ANALYST_SCOUT: 'ANALYST_SCOUT',
  STAFF: 'STAFF',
  ADMIN: 'ADMIN',
  USER: 'USER',
};

// =============================================================================
// ROLE HIERARCHY (higher number = more access)
// =============================================================================
export const ROLE_HIERARCHY = {
  ADMIN: 100,
  ATHLETIC_DIRECTOR: 90,
  HEAD_COACH: 80,
  ASSISTANT_COACH: 70,
  ANALYST_SCOUT: 60,
  STAFF: 50,
  USER: 40,
};

// =============================================================================
// ACCESS LEVELS
// =============================================================================
export const ACCESS_LEVELS = {
  NONE: 0,
  READ: 1,
  WRITE: 2,
  ADMIN: 3,
};

// =============================================================================
// DOMAINS (access scopes)
// =============================================================================
export const DOMAINS = {
  NIL: 'NIL',
  SCOUTING: 'SCOUTING',
  TRAINING: 'TRAINING',
  OPS: 'OPS',
  ANALYTICS: 'ANALYTICS',
  REPORTS: 'REPORTS',
};

// =============================================================================
// ROLE DEFINITIONS
// =============================================================================
export const ROLE_DEFINITIONS = {
  ATHLETIC_DIRECTOR: {
    id: 'ATHLETIC_DIRECTOR',
    name: 'Athletic Director',
    shortName: 'AD',
    description: 'Department-level oversight, approvals, and compliance',
    tier: 'executive',
    icon: 'Building2',
    color: 'purple',
    defaultDashboard: '/my-dashboard',
    features: [
      'executive_summaries',
      'department_reports',
      'budget_overview',
      'nil_oversight',
      'approval_queue',
    ],
  },

  HEAD_COACH: {
    id: 'HEAD_COACH',
    name: 'Head Coach',
    shortName: 'HC',
    description: 'Full team access, analytics, recruiting decisions',
    tier: 'operational',
    icon: 'UserCog',
    color: 'blue',
    defaultDashboard: '/my-dashboard',
    features: [
      'player_development',
      'game_prep',
      'practice_planning',
      'video_analysis',
      'recruiting_decisions',
    ],
  },

  ASSISTANT_COACH: {
    id: 'ASSISTANT_COACH',
    name: 'Assistant Coach',
    shortName: 'AC',
    description: 'Team-specific functions, video analysis, practice planning',
    tier: 'operational',
    icon: 'Users',
    color: 'green',
    defaultDashboard: '/my-dashboard',
    features: [
      'player_development',
      'video_analysis',
      'practice_planning',
    ],
  },

  ANALYST_SCOUT: {
    id: 'ANALYST_SCOUT',
    name: 'Analyst/Scout',
    shortName: 'Scout',
    description: 'Full analytical capabilities, scouting, report generation',
    tier: 'analytical',
    icon: 'Search',
    color: 'orange',
    defaultDashboard: '/video-analytics',
    features: [
      'deep_analysis',
      'comparative_scouting',
      'statistical_tools',
      'export_reports',
      'moe_full_access',
    ],
  },

  STAFF: {
    id: 'STAFF',
    name: 'Staff',
    shortName: 'Staff',
    description: 'Limited operational access, basic views',
    tier: 'support',
    icon: 'Briefcase',
    color: 'gray',
    defaultDashboard: '/dashboard',
    features: [
      'schedule_view',
      'basic_reports',
    ],
  },

  ADMIN: {
    id: 'ADMIN',
    name: 'Administrator',
    shortName: 'Admin',
    description: 'System configuration, user management, full access',
    tier: 'system',
    icon: 'Shield',
    color: 'red',
    defaultDashboard: '/user-management',
    features: [
      'user_management',
      'system_configuration',
      'full_access',
    ],
  },

  USER: {
    id: 'USER',
    name: 'User',
    shortName: 'User',
    description: 'Standard platform user with full feature access',
    tier: 'standard',
    icon: 'User',
    color: 'blue',
    defaultDashboard: '/dashboard',
    features: [
      'player_development',
      'game_prep',
      'video_analysis',
      'practice_planning',
      'deep_analysis',
      'statistical_tools',
      'export_reports',
    ],
  },
};

// =============================================================================
// ROLE PERMISSIONS (Domain Access by Role)
// =============================================================================
export const ROLE_PERMISSIONS = {
  ATHLETIC_DIRECTOR: {
    [DOMAINS.NIL]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.SCOUTING]: ACCESS_LEVELS.READ,
    [DOMAINS.TRAINING]: ACCESS_LEVELS.READ,
    [DOMAINS.OPS]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.ANALYTICS]: ACCESS_LEVELS.READ,
    [DOMAINS.REPORTS]: ACCESS_LEVELS.ADMIN,
  },

  HEAD_COACH: {
    [DOMAINS.NIL]: ACCESS_LEVELS.WRITE,
    [DOMAINS.SCOUTING]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.TRAINING]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.OPS]: ACCESS_LEVELS.READ,
    [DOMAINS.ANALYTICS]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.REPORTS]: ACCESS_LEVELS.WRITE,
  },

  ASSISTANT_COACH: {
    [DOMAINS.NIL]: ACCESS_LEVELS.READ,
    [DOMAINS.SCOUTING]: ACCESS_LEVELS.WRITE,
    [DOMAINS.TRAINING]: ACCESS_LEVELS.WRITE,
    [DOMAINS.OPS]: ACCESS_LEVELS.READ,
    [DOMAINS.ANALYTICS]: ACCESS_LEVELS.WRITE,
    [DOMAINS.REPORTS]: ACCESS_LEVELS.WRITE,
  },

  ANALYST_SCOUT: {
    [DOMAINS.NIL]: ACCESS_LEVELS.READ,
    [DOMAINS.SCOUTING]: ACCESS_LEVELS.WRITE,
    [DOMAINS.TRAINING]: ACCESS_LEVELS.READ,
    [DOMAINS.OPS]: ACCESS_LEVELS.NONE,
    [DOMAINS.ANALYTICS]: ACCESS_LEVELS.WRITE,
    [DOMAINS.REPORTS]: ACCESS_LEVELS.WRITE,
  },

  STAFF: {
    [DOMAINS.NIL]: ACCESS_LEVELS.NONE,
    [DOMAINS.SCOUTING]: ACCESS_LEVELS.NONE,
    [DOMAINS.TRAINING]: ACCESS_LEVELS.READ,
    [DOMAINS.OPS]: ACCESS_LEVELS.WRITE,
    [DOMAINS.ANALYTICS]: ACCESS_LEVELS.NONE,
    [DOMAINS.REPORTS]: ACCESS_LEVELS.READ,
  },

  ADMIN: {
    [DOMAINS.NIL]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.SCOUTING]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.TRAINING]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.OPS]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.ANALYTICS]: ACCESS_LEVELS.ADMIN,
    [DOMAINS.REPORTS]: ACCESS_LEVELS.ADMIN,
  },

  USER: {
    [DOMAINS.NIL]: ACCESS_LEVELS.READ,
    [DOMAINS.SCOUTING]: ACCESS_LEVELS.WRITE,
    [DOMAINS.TRAINING]: ACCESS_LEVELS.WRITE,
    [DOMAINS.OPS]: ACCESS_LEVELS.READ,
    [DOMAINS.ANALYTICS]: ACCESS_LEVELS.WRITE,
    [DOMAINS.REPORTS]: ACCESS_LEVELS.WRITE,
  },
};

// =============================================================================
// ROUTE ACCESS BY ROLE
// =============================================================================
export const ROLE_ROUTE_ACCESS = {
  ATHLETIC_DIRECTOR: [
    '/dashboard',
    '/my-dashboard',
    '/reports',
    '/settings',
    '/roster',
    '/player-roster',
    '/my-team',
    '/my-team-player',
    '/integrations',
  ],

  HEAD_COACH: [
    '/dashboard',
    '/my-dashboard',
    '/ai-assistant',
    '/slam-ai',
    '/ask-benny',
    '/custom-dashboards',
    '/saved-pages',
    '/my-team',
    '/my-team-player',
    '/video-analytics',
    '/video-analyzer',
    '/development',
    '/roster',
    '/player-roster',
    '/transfer-portal',
    '/scouting',
    '/ai-analysis-hub',
    '/agent-universe',
    '/reports',
    '/settings',
  ],

  ASSISTANT_COACH: [
    '/dashboard',
    '/my-dashboard',
    '/ai-assistant',
    '/slam-ai',
    '/ask-benny',
    '/custom-dashboards',
    '/saved-pages',
    '/my-team',
    '/my-team-player',
    '/video-analytics',
    '/video-analyzer',
    '/development',
    '/roster',
    '/player-roster',
    '/reports',
  ],

  ANALYST_SCOUT: [
    '/dashboard',
    '/my-dashboard',
    '/ai-assistant',
    '/slam-ai',
    '/ask-benny',
    '/custom-dashboards',
    '/saved-pages',
    '/video-analytics',
    '/video-analyzer',
    '/transfer-portal',
    '/scouting',
    '/roster',
    '/player-roster',
    '/ai-analysis-hub',
    '/agent-universe',
    '/reports',
  ],

  STAFF: [
    '/dashboard',
    '/my-dashboard',
    '/settings',
    '/reports',
  ],

  ADMIN: [
    '/dashboard',
    '/my-dashboard',
    '/ai-assistant',
    '/slam-ai',
    '/ask-benny',
    '/custom-dashboards',
    '/saved-pages',
    '/my-team',
    '/my-team-player',
    '/video-analytics',
    '/video-analyzer',
    '/development',
    '/roster',
    '/player-roster',
    '/transfer-portal',
    '/scouting',
    '/pre-college',
    '/pre-college-player',
    '/ai-analysis-hub',
    '/agent-universe',
    '/reports',
    '/settings',
    '/integrations',
    '/user-management',
    '/permissions',
    '/the-wire-report',
  ],

  USER: [
    '/dashboard',
    '/my-dashboard',
    '/ai-assistant',
    '/slam-ai',
    '/ask-benny',
    '/custom-dashboards',
    '/saved-pages',
    '/my-team',
    '/my-team-player',
    '/video-analytics',
    '/video-analyzer',
    '/development',
    '/roster',
    '/player-roster',
    '/transfer-portal',
    '/scouting',
    '/pre-college',
    '/pre-college-player',
    '/ai-analysis-hub',
    '/agent-universe',
    '/reports',
    '/settings',
    '/integrations',
    '/the-wire-report',
  ],
};

// =============================================================================
// ANALYSIS MODE ACCESS BY ROLE
// =============================================================================
export const ANALYSIS_MODE_ACCESS = {
  'pitch-tipping': ['ANALYST_SCOUT', 'ADMIN'],
  'big-league-hitter': ['ANALYST_SCOUT', 'HEAD_COACH', 'ADMIN'],
  'basketball-analysis': ['ANALYST_SCOUT', 'HEAD_COACH', 'ASSISTANT_COACH', 'ADMIN'],
};

// =============================================================================
// MoE EXPERT ACCESS BY ROLE
// =============================================================================
export const EXPERT_ROLE_ACCESS = {
  // Baseball Experts
  BIOMECHANICS_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ASSISTANT_COACH', 'ADMIN'],
  VISUAL_ANALYSIS_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ASSISTANT_COACH', 'ADMIN'],
  EXPLOITATION_STRATEGY_EXPERT: ['ANALYST_SCOUT', 'ADMIN'], // Sensitive intel
  GAME_DYNAMICS_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ADMIN'],
  BROADCAST_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ATHLETIC_DIRECTOR', 'ADMIN'],
  STATISTICAL_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ATHLETIC_DIRECTOR', 'ADMIN'],

  // Basketball Experts
  SHOOTING_BIOMECHANICS_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ASSISTANT_COACH', 'ADMIN'],
  FOOTWORK_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ASSISTANT_COACH', 'ADMIN'],
  MOVEMENT_PATTERN_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ADMIN'],
  COACHING_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ASSISTANT_COACH', 'ADMIN'],
  GAME_CONTEXT_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ADMIN'],

  // Hitter Experts
  VISUAL_PROCESSING_EXPERT: ['ANALYST_SCOUT', 'ADMIN'],
  MOTOR_TIMING_EXPERT: ['ANALYST_SCOUT', 'HEAD_COACH', 'ADMIN'],
  DECISION_SCIENCE_EXPERT: ['ANALYST_SCOUT', 'ADMIN'],
  MENTAL_PERFORMANCE_EXPERT: ['ANALYST_SCOUT', 'ADMIN'],
};

// =============================================================================
// ROLE MIGRATION MAP (Old roles -> New roles)
// =============================================================================
export const ROLE_MIGRATION_MAP = {
  'Administrator': 'ADMIN',
  'Coach': 'HEAD_COACH',
  'Recruiter': 'ANALYST_SCOUT',
  'Player Development': 'ASSISTANT_COACH',
  'Operations Staff': 'STAFF',
  'Read Only': 'STAFF',
  'User': 'USER',
  'USER': 'USER',
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get role definition by ID
 * @param {string} roleId - Role ID
 * @returns {Object|null} Role definition
 */
export function getRoleDefinition(roleId) {
  return ROLE_DEFINITIONS[roleId] || null;
}

/**
 * Get role display name
 * @param {string} roleId - Role ID
 * @returns {string} Display name
 */
export function getRoleDisplayName(roleId) {
  return ROLE_DEFINITIONS[roleId]?.name || roleId;
}

/**
 * Check if user has minimum role level
 * @param {string} userRole - User's role ID
 * @param {string} requiredRole - Minimum required role ID
 * @returns {boolean} Whether user has sufficient access
 */
export function hasMinimumRole(userRole, requiredRole) {
  const userLevel = ROLE_HIERARCHY[userRole] || 0;
  const requiredLevel = ROLE_HIERARCHY[requiredRole] || 0;
  return userLevel >= requiredLevel;
}

/**
 * Check if role has access to a domain at a specific level
 * @param {string} roleId - Role ID
 * @param {string} domain - Domain name
 * @param {string} accessLevel - Required access level (READ, WRITE, ADMIN)
 * @returns {boolean} Whether role has access
 */
export function hasRoleDomainAccess(roleId, domain, accessLevel = 'READ') {
  const rolePerms = ROLE_PERMISSIONS[roleId];
  if (!rolePerms) return false;

  const required = ACCESS_LEVELS[accessLevel] || ACCESS_LEVELS.READ;
  const actual = rolePerms[domain] || ACCESS_LEVELS.NONE;

  return actual >= required;
}

/**
 * Check if role can access a route
 * @param {string} roleId - Role ID
 * @param {string} route - Route path
 * @returns {boolean} Whether role can access route
 */
export function canRoleAccessRoute(roleId, route) {
  const allowedRoutes = ROLE_ROUTE_ACCESS[roleId];
  if (!allowedRoutes) return false;
  return allowedRoutes.includes(route);
}

/**
 * Check if role can access an analysis mode
 * @param {string} roleId - Role ID
 * @param {string} modeId - Analysis mode ID
 * @returns {boolean} Whether role can access mode
 */
export function canRoleAccessAnalysisMode(roleId, modeId) {
  const allowedRoles = ANALYSIS_MODE_ACCESS[modeId];
  if (!allowedRoles) return false;
  return allowedRoles.includes(roleId);
}

/**
 * Check if role can access an MoE expert
 * @param {string} roleId - Role ID
 * @param {string} expertId - Expert ID
 * @returns {boolean} Whether role can access expert
 */
export function canRoleAccessExpert(roleId, expertId) {
  const allowedRoles = EXPERT_ROLE_ACCESS[expertId];
  if (!allowedRoles) return true; // Default to allowed if not specified
  return allowedRoles.includes(roleId);
}

/**
 * Get all routes accessible by a role
 * @param {string} roleId - Role ID
 * @returns {string[]} Array of accessible routes
 */
export function getAccessibleRoutes(roleId) {
  return ROLE_ROUTE_ACCESS[roleId] || [];
}

/**
 * Get default dashboard for a role
 * @param {string} roleId - Role ID
 * @returns {string} Default dashboard route
 */
export function getDefaultDashboard(roleId) {
  return ROLE_DEFINITIONS[roleId]?.defaultDashboard || '/dashboard';
}

/**
 * Get all role definitions as array (for UI dropdowns)
 * @returns {Array} Array of role definitions
 */
export function getAllRoles() {
  return Object.values(ROLE_DEFINITIONS);
}

/**
 * Map old role name to new role ID
 * @param {string} oldRoleName - Old role name
 * @returns {string} New role ID
 */
export function migrateRoleName(oldRoleName) {
  return ROLE_MIGRATION_MAP[oldRoleName] || 'STAFF';
}

// Default export
export default {
  ROLE_TYPES,
  ROLE_HIERARCHY,
  ACCESS_LEVELS,
  DOMAINS,
  ROLE_DEFINITIONS,
  ROLE_PERMISSIONS,
  ROLE_ROUTE_ACCESS,
  ANALYSIS_MODE_ACCESS,
  EXPERT_ROLE_ACCESS,
  ROLE_MIGRATION_MAP,
  getRoleDefinition,
  getRoleDisplayName,
  hasMinimumRole,
  hasRoleDomainAccess,
  canRoleAccessRoute,
  canRoleAccessAnalysisMode,
  canRoleAccessExpert,
  getAccessibleRoutes,
  getDefaultDashboard,
  getAllRoles,
  migrateRoleName,
};
